import { baseApi } from 'services/baseApi';
import { setAuthenticated } from 'app/auth/slices';
import { LocalStorageService } from 'services';
import i18n from 'services/i18n';
import { selectUserConfigs } from 'app/userPreferences/selectors';
import { saveUserConfigs, saveUserConfigKey } from 'app/userPreferences/slices';
import { AppConstants } from 'constants/index';
import { cacheTags } from 'constants/cacheTags';
import { UserConfig as UserConfigType, UserConfigs } from 'app/userPreferences/types';
import type { RootState } from 'store';
import { getMessage } from 'utils/helpers';

export const userPreferencesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserConfigs: builder.query<UserConfigs, void>({
      query: () => {
        const { Admin, UserConfig } = AppConstants.api;
        return {
          url: [Admin, UserConfig],
        };
      },
      providesTags: [cacheTags.UserConfigs],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(saveUserConfigs(data));
          LocalStorageService.set('configs', data);
        } catch {
          LocalStorageService.remove('auth');
          dispatch(setAuthenticated(false));
        }
      },
    }),
    createOrEditUserConfig: builder.mutation<void, UserConfigType>({
      query: ({ skipGlobalLoading, ...data }) => {
        const { Admin, UserConfig } = AppConstants.api;
        return {
          method: AppConstants.methods.POST,
          url: [Admin, UserConfig],
          data,
          meta: { skipGlobalLoading },
        };
      },
      async onQueryStarted(arg: UserConfigType, { dispatch, getState }) {
        const state = getState() as RootState;
        const data = selectUserConfigs(state);
        const updated = {
          ...data,
          [arg.key]: arg.configValue,
        };
        LocalStorageService.set('configs', updated);
        dispatch(saveUserConfigKey(arg));
      },
    }),
    deleteUserConfig: builder.mutation<void, string>({
      query: (key) => {
        const { Admin, UserConfig } = AppConstants.api;
        return {
          method: AppConstants.methods.DELETE,
          url: [Admin, UserConfig, key],
          meta: {
            successMessage: getMessage(i18n.t('title:userConfig'), i18n.t('notification:slDeleted')),
          },
        };
      },
      invalidatesTags: [cacheTags.UserConfigs],
    }),
  }),
});

export const { useLazyGetUserConfigsQuery, useCreateOrEditUserConfigMutation, useDeleteUserConfigMutation } = userPreferencesApi;
